import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  CashControlsData,
  CashControlsFormState,
  CashControlsValidationErrors,
} from '../types/index';
import { computeCashAvailable } from '../lib/cashCalculations';
import { validateNumericInput, parseNumericValue } from '../lib/cashValidation';
import { supabase } from '../lib/supabase';

const DEFAULT_FORM_STATE: CashControlsFormState = {
  accountValue: '0',
  settledAvailableCash: '0',
  nonMarginBuyingPower: '0',
  marginBuyingPower: '0',
  cashReservedForOptions: '0',
  targetCashReserve: '0',
  notes: '',
};

function dataToFormState(data: CashControlsData): CashControlsFormState {
  return {
    accountValue: data.accountValue.toString(),
    settledAvailableCash: data.settledAvailableCash.toString(),
    nonMarginBuyingPower: data.nonMarginBuyingPower.toString(),
    marginBuyingPower: data.marginBuyingPower.toString(),
    cashReservedForOptions: data.cashReservedForOptions.toString(),
    targetCashReserve: data.targetCashReserve.toString(),
    notes: data.notes || '',
  };
}

function computeCalculatedValue(formState: CashControlsFormState): number {
  return computeCashAvailable(
    parseNumericValue(formState.settledAvailableCash),
    parseNumericValue(formState.nonMarginBuyingPower),
    parseNumericValue(formState.marginBuyingPower),
    parseNumericValue(formState.cashReservedForOptions),
    parseNumericValue(formState.targetCashReserve)
  );
}

function computeIsDirty(formState: CashControlsFormState, data: CashControlsData | null): boolean {
  if (!data) return false;
  const numericFields: (keyof Omit<CashControlsFormState, 'notes'>)[] = [
    'accountValue',
    'settledAvailableCash',
    'nonMarginBuyingPower',
    'marginBuyingPower',
    'cashReservedForOptions',
    'targetCashReserve',
  ];
  for (const field of numericFields) {
    if (parseNumericValue(formState[field]) !== data[field]) {
      return true;
    }
  }
  if (formState.notes !== (data.notes || '')) {
    return true;
  }
  return false;
}

export interface UseCashControlsReturn {
  data: CashControlsData | null;
  formState: CashControlsFormState;
  errors: CashControlsValidationErrors;
  calculatedValue: number;
  isLoading: boolean;
  isSaving: boolean;
  isExpanded: boolean;
  isDirty: boolean;
  loadError: string | null;
  saveError: string | null;
  saveSuccess: boolean;
  setField: (field: keyof CashControlsFormState, value: string) => void;
  save: () => Promise<void>;
  cancel: () => void;
  toggleExpanded: () => void;
  retry: () => void;
}

export function useCashControls(accountSlug: string): UseCashControlsReturn {
  const [data, setData] = useState<CashControlsData | null>(null);
  const [formState, setFormState] = useState<CashControlsFormState>(DEFAULT_FORM_STATE);
  const [errors, setErrors] = useState<CashControlsValidationErrors>({});
  const [calculatedValue, setCalculatedValue] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  const saveSuccessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Recompute calculated value whenever formState changes
  useEffect(() => {
    setCalculatedValue(computeCalculatedValue(formState));
  }, [formState]);

  // Fetch data on mount and when accountSlug changes
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const { data: row, error } = await supabase
        .from('account_cash_controls')
        .select('*')
        .eq('account_slug', accountSlug)
        .eq('is_active', true)
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw new Error(error.message || 'Failed to load cash controls data.');
      }

      // Map snake_case DB row to camelCase CashControlsData
      const result: CashControlsData = row ? {
        accountSlug: row.account_slug,
        accountValue: row.account_value || 0,
        settledAvailableCash: row.settled_available_cash || 0,
        nonMarginBuyingPower: row.non_margin_buying_power || 0,
        marginBuyingPower: row.margin_buying_power || 0,
        cashReservedForOptions: row.cash_reserved_for_options || 0,
        targetCashReserve: row.target_cash_reserve || 0,
        cashAvailableForNewTrades: row.cash_available_for_new_trades || 0,
        notes: row.notes || '',
        updatedAt: row.updated_at,
      } : {
        accountSlug,
        accountValue: 0,
        settledAvailableCash: 0,
        nonMarginBuyingPower: 0,
        marginBuyingPower: 0,
        cashReservedForOptions: 0,
        targetCashReserve: 0,
        cashAvailableForNewTrades: 0,
        notes: '',
        updatedAt: null,
      };

      setData(result);
      const newFormState = dataToFormState(result);
      setFormState(newFormState);
      setErrors({});
      setIsDirty(false);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setLoadError(err.message || 'Failed to load cash controls data.');
      } else {
        setLoadError('An unexpected error occurred while loading data.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [accountSlug]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup save success timer on unmount
  useEffect(() => {
    return () => {
      if (saveSuccessTimerRef.current) {
        clearTimeout(saveSuccessTimerRef.current);
      }
    };
  }, []);

  const setField = useCallback(
    (field: keyof CashControlsFormState, value: string) => {
      setFormState((prev) => {
        const updated = { ...prev, [field]: value };
        // Validate if it's a numeric field
        if (field !== 'notes') {
          const validation = validateNumericInput(value);
          setErrors((prevErrors) => {
            if (!validation.valid) {
              return { ...prevErrors, [field]: validation.error };
            }
            const { [field]: _, ...rest } = prevErrors;
            return rest;
          });
        }
        // Recompute dirty
        setIsDirty(computeIsDirty(updated, data));
        return updated;
      });
    },
    [data]
  );

  const save = useCallback(async () => {
    // Validate all fields before saving
    const numericFields: (keyof Omit<CashControlsFormState, 'notes'>)[] = [
      'accountValue',
      'settledAvailableCash',
      'nonMarginBuyingPower',
      'marginBuyingPower',
      'cashReservedForOptions',
      'targetCashReserve',
    ];

    const newErrors: CashControlsValidationErrors = {};
    for (const field of numericFields) {
      const validation = validateNumericInput(formState[field]);
      if (!validation.valid) {
        newErrors[field] = validation.error;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const values = {
        account_value: parseNumericValue(formState.accountValue),
        settled_available_cash: parseNumericValue(formState.settledAvailableCash),
        non_margin_buying_power: parseNumericValue(formState.nonMarginBuyingPower),
        margin_buying_power: parseNumericValue(formState.marginBuyingPower),
        cash_reserved_for_options: parseNumericValue(formState.cashReservedForOptions),
        target_cash_reserve: parseNumericValue(formState.targetCashReserve),
        cash_available_for_new_trades: parseNumericValue(formState.nonMarginBuyingPower),
        notes: formState.notes,
      };

      const { data: existing } = await supabase
        .from('account_cash_controls')
        .select('id')
        .eq('account_slug', accountSlug)
        .eq('is_active', true)
        .limit(1)
        .single();

      let resultRow: any;

      if (existing) {
        const { data: updated, error } = await supabase
          .from('account_cash_controls')
          .update(values)
          .eq('account_slug', accountSlug)
          .eq('is_active', true)
          .select()
          .single();
        if (error) throw new Error(error.message || 'Save failed');
        resultRow = updated;
      } else {
        const { data: inserted, error } = await supabase
          .from('account_cash_controls')
          .insert({
            account_slug: accountSlug,
            is_active: true,
            user_id: '00000000-0000-0000-0000-000000000000',
            ...values,
          })
          .select()
          .single();
        if (error) throw new Error(error.message || 'Save failed');
        resultRow = inserted;
      }

      const result: CashControlsData = {
        accountSlug: resultRow.account_slug,
        accountValue: resultRow.account_value || 0,
        settledAvailableCash: resultRow.settled_available_cash || 0,
        nonMarginBuyingPower: resultRow.non_margin_buying_power || 0,
        marginBuyingPower: resultRow.margin_buying_power || 0,
        cashReservedForOptions: resultRow.cash_reserved_for_options || 0,
        targetCashReserve: resultRow.target_cash_reserve || 0,
        cashAvailableForNewTrades: resultRow.cash_available_for_new_trades || 0,
        notes: resultRow.notes || '',
        updatedAt: resultRow.updated_at,
      };

      setData(result);
      const newFormState = dataToFormState(result);
      setFormState(newFormState);
      setErrors({});
      setIsDirty(false);

      // Show save success for 3 seconds
      setSaveSuccess(true);
      if (saveSuccessTimerRef.current) {
        clearTimeout(saveSuccessTimerRef.current);
      }
      saveSuccessTimerRef.current = setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setSaveError(err.message || 'Failed to save. Please try again.');
      } else {
        setSaveError('An unexpected error occurred while saving.');
      }
    } finally {
      setIsSaving(false);
    }
  }, [accountSlug, formState]);

  const cancel = useCallback(() => {
    if (data) {
      setFormState(dataToFormState(data));
    } else {
      setFormState(DEFAULT_FORM_STATE);
    }
    setErrors({});
    setIsDirty(false);
  }, [data]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const retry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    formState,
    errors,
    calculatedValue,
    isLoading,
    isSaving,
    isExpanded,
    isDirty,
    loadError,
    saveError,
    saveSuccess,
    setField,
    save,
    cancel,
    toggleExpanded,
    retry,
  };
}
